import { fabric } from 'fabric';
import { Group } from 'fabric/fabric-impl';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import alignment from 'modules/overlay-canvas/addons/alignment/alignment';
import history from 'modules/overlay-canvas/addons/history';
import TeamPeriodFouls, {
  OrientationEnum,
  ShapeEnum,
} from 'modules/overlay-canvas/addons/placeholders/complex/team-period-fouls';
import SnapToGrid from 'modules/overlay-canvas/addons/snap-to-grid';
import UDText from 'ud-ui/components/text';
import { round } from 'shared/utils/round';
import { selectFabricManager } from 'modules/template-editor/store/editor/selectors';
import ColorPicker from 'modules/template-editor/ui/components/elements/color-picker';
import NumberInput from 'modules/template-editor/ui/components/elements/number-input';
import ChoosingOrientation from './components/choosing-orientation';
import ChoosingShape from './components/choosing-shape';
import ChoosingTeam from './components/choosing-team';
import LayerControls from './components/layer-controls';
import TransformSection from './shared/transform-section';
import LockPosition from './components/lock-position';
import { Item, LongDivider, ShortDivider } from './styles';
import { theme } from 'styles/theme/theme';

interface IProps {
  object: Group;
}

function ComplexPlaceholder(props: IProps) {
  const manager = useSelector(selectFabricManager);
  const { object } = props;

  const data = useMemo(() => {
    return object.data;
  }, [object.data]);
  const point = useMemo(() => {
    const { x, y } = object.getPointByOrigin('left', 'top');
    return { x: round(x), y: round(y) };
  }, [object]);
  const bounding = useMemo(() => {
    // const { width, height } = object.getBoundingRect();
    // const { width, height } = object.getObjects('rect')[0].getBoundingRect();
    const width = object.getScaledWidth();
    const height = object.getScaledHeight();
    return { width: round(width), height: round(height) };
  }, [object]);
  const name = useMemo(() => {
    return data?.name ?? data?.indicator;
  }, [data?.indicator, data?.name]);
  const lockMovementX = object?.lockMovementX;

  const [hasTeam, setHasTeam] = useState(data?.hasTeam ?? data?.teamIndex !== undefined);
  const [position, setPosition] = useState({ x: point.x, y: point.y });
  const [size, setSize] = useState(bounding);
  const [teamIndex, setTeamIndex] = useState(data?.team ?? data?.teamIndex);
  const [backgroundColor, setBackgroundColor] = useState(data?.backgroundColor ?? '#FFFFFF00');
  const [elementColor, setElementColor] = useState(data?.elementColor ?? theme.colors.SurfaceRaisinBlack50);
  const [borderColor, setBorderColor] = useState(data?.borderColor ?? '#404861');
  const [textColor, setTextColor] = useState(data?.textColor ?? theme.colors.SurfaceWhite);
  const [orientation, setOrientation] = useState(data?.orientation ?? OrientationEnum.HORIZONTAL);
  const [isPositionLock, setIsPositionLock] = useState(lockMovementX ?? false);
  const [maxCount, setMaxCount] = useState(data?.maxCount ?? 5);
  const [spacing, setSpacing] = useState(data?.spacing ?? 10);
  const [shape, setShape] = useState(data?.shape ?? ShapeEnum.CIRCLE);

  useEffect(() => {
    if (name === 'teamComposition') {
      object.setControlsVisibility({
        mt: false,
        mb: false,
        ml: false,
        mr: false,
        bl: true,
        br: true,
        tl: true,
        tr: true,
      });
    }

    object.setControlsVisibility({
      mtr: false,
    });

    setPosition(point);
    setSize(bounding);
    setIsPositionLock(lockMovementX ?? false);
  }, [bounding, lockMovementX, name, object, point]);

  useEffect(() => {
    if (!manager) return;
    manager.canvas.uniScaleKey = undefined;

    return () => {
      manager.canvas.uniScaleKey = 'shiftKey';
    };
  }, [manager]);

  useEffect(() => {
    setHasTeam(data?.hasTeam ?? data?.teamIndex !== undefined);
    if (data?.hasTeam) setTeamIndex(data?.team);
    if (data?.orientation) setOrientation(data.orientation);
    if (data?.maxCount) {
      setMaxCount(data.maxCount);
    }
    if (data?.spacing) setSpacing(data.spacing);
    if (data?.shape) setShape(data.shape);
  }, [data?.hasTeam, data?.team, data?.teamIndex, data?.orientation, data?.maxCount, data?.spacing, data?.shape]);

  const onFocusField = useCallback(() => {
    manager?.render();
    history.saveState();
  }, [manager]);

  const onChangePosition = useCallback(
    (x: number, y: number) => {
      object.setPositionByOrigin(new fabric.Point(x, y), 'left', 'top');
      setPosition({ x: round(x), y: round(y) });
      manager?.render();
    },
    [manager, object],
  );

  const onChangeSize = useCallback(
    (width: number, height: number) => {
      object.set({ scaleX: width / (object?.width ?? 1), scaleY: height / (object?.height ?? 1) });
      setSize({ width: round(width), height: round(height) });
      SnapToGrid.transformAfterScaling(object);
      manager?.render();
    },
    [manager, object],
  );

  const onChangeTeam = useCallback(
    (teamIndex: 0 | 1) => {
      const oldTeamIndex = object.data?.teamIndex;
      if (oldTeamIndex !== undefined) {
        object.data = { ...object.data, teamIndex };
      } else if (!object.data?.team === undefined) {
        object.data = { ...object.data, team: teamIndex };
      }

      history.saveState();
      setTeamIndex(teamIndex);
    },
    [object],
  );

  const changeBackgroundColor = useCallback(
    (color: string) => {
      setBackgroundColor(color);
      object.data = { ...object.data, backgroundColor: color };
      const background = object.getObjects('rect')[0];
      if (background) {
        background.set({ fill: color });
      }
      manager?.render();
      history.saveState();
    },
    [manager, object],
  );

  const changeElementColor = useCallback(
    (color: string) => {
      setElementColor(color);
      object.data = { ...object.data, elementColor: color };

      switch (object.data?.indicator) {
        case 'teamComposition':
          const elements = object.getObjects('group') as Group[];
          if (elements.length > 0) {
            for (const element of elements) {
              const subElements = element.getObjects('group') as Group[];
              if (subElements.length > 0) {
                for (const subElement of subElements) {
                  const rect = subElement.getObjects('rect')[0];
                  if (rect) {
                    rect.set({ fill: color });
                  }
                }
              }
            }
          }
          break;
        case 'teamPeriodFouls':
          // Обновление цвета кругов/квадратов фолов
          const indicators = object.getObjects().filter((obj) => obj.type === 'circle' || obj.type === 'rect');

          if (indicators.length > 0) {
            for (const indicator of indicators) {
              if (indicator !== object.getObjects('rect')[0]) {
                // Не обновляем фон
                indicator.set({ fill: color });
              }
            }
          }
          break;
        default:
          break;
      }
      // Добавляем вызов transformAfterScaling для индикатора teamPeriodFouls
      if (object.data?.indicator === 'teamPeriodFouls') {
        SnapToGrid.transformAfterScaling(object);
      }
      manager?.render();
      history.saveState();
    },
    [manager, object],
  );

  const changeBorderColor = useCallback(
    (color: string) => {
      setBorderColor(color);
      object.data = { ...object.data, borderColor: color };
      const elements = object.getObjects('group') as Group[];
      if (elements.length > 0) {
        for (const element of elements) {
          const subElements = element.getObjects('group') as Group[];
          if (subElements.length > 0) {
            for (const subElement of subElements) {
              const rect = subElement.getObjects('rect')[0];
              if (rect) {
                rect.set({ stroke: color });
              }
            }
          }
        }
      }
      manager?.render();
      history.saveState();
    },
    [manager, object],
  );

  const changeTextColor = useCallback(
    (color: string) => {
      setTextColor(color);
      object.data = { ...object.data, textColor: color };
      const elements = object.getObjects('group') as Group[];
      if (elements.length > 0) {
        for (const element of elements) {
          const subElements = element.getObjects('group') as Group[];
          if (subElements.length > 0) {
            for (const subElement of subElements) {
              const text = subElement.getObjects('text')[0];
              if (text) {
                text.set({ fill: color });
              }
            }
          }
        }
      }
      manager?.render();
      history.saveState();
    },
    [manager, object],
  );

  const changeMaxCount = useCallback(
    (newMaxCount: number) => {
      if (!manager || !object) return;
      if (newMaxCount < 1) newMaxCount = 1;
      if (newMaxCount > 20) newMaxCount = 20;

      setMaxCount(newMaxCount);
      object.data = { ...object.data, maxCount: newMaxCount };

      // Получаем фон и удаляем все индикаторы
      const background = object.getObjects('rect')[0];
      const indicators = object.getObjects().filter((obj) => (obj.type === 'circle' || obj.type === 'rect') && obj !== background);

      // Удаляем старые индикаторы
      indicators.forEach((indicator) => {
        object.remove(indicator);
      });

      // Получаем актуальные размеры фона
      const backgroundWidth = background.getScaledWidth();
      const backgroundHeight = background.getScaledHeight();

      // Создаем новые индикаторы
      const newIndicators = TeamPeriodFouls.createFoulsIndicators({
        maxCount: newMaxCount,
        color: object.data.elementColor || '#FFA500',
        spacing: object.data.spacing ?? 10,
        orientation: object.data.orientation || OrientationEnum.HORIZONTAL,
        shape: object.data.shape || ShapeEnum.CIRCLE,
        width: backgroundWidth,
        height: backgroundHeight,
      });

      // Добавляем новые индикаторы в группу
      newIndicators.forEach((indicator) => {
        object.add(indicator);
      });

      object.setCoords();
      // Добавляем вызов transformAfterScaling
      SnapToGrid.transformAfterScaling(object);
      manager.render();
      history.saveState();
    },
    [manager, object],
  );

  const changeSpacing = useCallback(
    (newSpacing: number) => {
      if (!manager || !object) return;

      // Округляем значение до целого числа
      newSpacing = Math.round(newSpacing);

      // Получаем актуальные размеры фона
      const background = object.getObjects('rect')[0];
      const backgroundWidth = background.getScaledWidth();
      const backgroundHeight = background.getScaledHeight();

      // Ограничиваем значение
      if (newSpacing < 0) newSpacing = 0;

      setSpacing(newSpacing);
      object.data = { ...object.data, spacing: newSpacing };

      // Получаем все индикаторы
      const indicators = object.getObjects().filter((obj) => (obj.type === 'circle' || obj.type === 'rect') && obj !== background);

      // Удаляем старые индикаторы
      indicators.forEach((indicator) => {
        object.remove(indicator);
      });

      // Создаем новые индикаторы
      const newIndicators = TeamPeriodFouls.createFoulsIndicators({
        maxCount: object.data.maxCount || 5,
        color: object.data.elementColor || '#FFA500',
        spacing: newSpacing,
        orientation: object.data.orientation || OrientationEnum.HORIZONTAL,
        shape: object.data.shape || ShapeEnum.CIRCLE,
        width: backgroundWidth,
        height: backgroundHeight,
      });

      // Добавляем новые индикаторы в группу
      newIndicators.forEach((indicator) => {
        object.add(indicator);
      });

      object.setCoords();
      // Добавляем вызов transformAfterScaling
      SnapToGrid.transformAfterScaling(object);
      manager.render();
      history.saveState();
    },
    [manager, object],
  );

  const changeShape = useCallback(
    (newShape: ShapeEnum) => {
      if (!manager || !object) return;

      setShape(newShape);
      object.data = { ...object.data, shape: newShape };

      // Получаем фон и удаляем все индикаторы
      const background = object.getObjects('rect')[0];
      const indicators = object.getObjects().filter((obj) => (obj.type === 'circle' || obj.type === 'rect') && obj !== background);

      // Удаляем старые индикаторы
      indicators.forEach((indicator) => {
        object.remove(indicator);
      });

      // Получаем актуальные размеры фона
      const backgroundWidth = background.getScaledWidth();
      const backgroundHeight = background.getScaledHeight();

      // Создаем новые индикаторы
      const newIndicators = TeamPeriodFouls.createFoulsIndicators({
        maxCount: object.data.maxCount || 5,
        color: object.data.elementColor || '#FFA500',
        spacing: object.data.spacing ?? 10,
        orientation: object.data.orientation || OrientationEnum.HORIZONTAL,
        shape: newShape,
        width: backgroundWidth,
        height: backgroundHeight,
      });

      // Добавляем новые индикаторы в группу
      newIndicators.forEach((indicator) => {
        object.add(indicator);
      });

      object.setCoords();
      // Добавляем вызов transformAfterScaling
      SnapToGrid.transformAfterScaling(object);
      manager.render();
      history.saveState();
    },
    [manager, object],
  );

  const changeOrientation = useCallback(
    (newOrientation: OrientationEnum) => {
      if (!manager || !object || newOrientation === orientation) return;

      setOrientation(newOrientation);
      object.data = { ...object.data, orientation: newOrientation };

      // Получаем фон и удаляем все индикаторы
      const background = object.getObjects('rect')[0];
      const indicators = object.getObjects().filter((obj) => (obj.type === 'circle' || obj.type === 'rect') && obj !== background);

      // Удаляем старые индикаторы
      indicators.forEach((indicator) => {
        object.remove(indicator);
      });

      // Получаем актуальные размеры фона
      const backgroundWidth = background.getScaledWidth();
      const backgroundHeight = background.getScaledHeight();

      // Создаем новые индикаторы с новой ориентацией, используя статический метод из TeamPeriodFouls
      const newIndicators = TeamPeriodFouls.createFoulsIndicators({
        maxCount: object.data.maxCount || 5,
        color: object.data.elementColor || '#FFA500',
        spacing: object.data.spacing ?? 10,
        orientation: newOrientation,
        shape: object.data.shape || ShapeEnum.CIRCLE,
        width: backgroundWidth,
        height: backgroundHeight,
      });

      // Добавляем новые индикаторы в группу
      newIndicators.forEach((indicator) => {
        object.add(indicator);
      });

      object.setCoords();
      // Добавляем вызов transformAfterScaling
      SnapToGrid.transformAfterScaling(object);
      manager.render();
      history.saveState();
    },
    [manager, object, orientation],
  );

  const showDebugInfo = true;
  const isDev = useMemo(() => {
    return process.env.NODE_ENV === 'development';
  }, []);

  const onClickAlignmentCenterVertical = () => {
    const newPos = alignment.single.centerVertical(object);
    if (!newPos) return;
    setPosition({ x: newPos.left, y: newPos.top });
  };

  const onClickAlignmentCenterHorizontal = () => {
    const newPos = alignment.single.centerHorizontal(object);
    if (!newPos) return;
    setPosition({ x: newPos.left, y: newPos.top });
  };

  const categoryName = useMemo(() => {
    switch (name) {
      case 'teamComposition':
        return 'Состав команды';
      case 'teamPeriodFouls':
        return 'Фолы команды в периоде';
      default:
        return 'Плейсхолдер';
    }
  }, [name]);

  return (
    <>
      <Item>
        <div>{categoryName}</div>
      </Item>
      <ShortDivider />
      <TransformSection
        position={position}
        size={size}
        onChangePosition={onChangePosition}
        onChangeSize={onChangeSize}
        onAlignCenterHorizontal={onClickAlignmentCenterHorizontal}
        onAlignCenterVertical={onClickAlignmentCenterVertical}
        onFocusField={onFocusField}
      />
      {hasTeam && (
        <>
          <LongDivider />
          <Item>
            <ChoosingTeam onChange={onChangeTeam} value={teamIndex} />
          </Item>
        </>
      )}
      {name === 'teamPeriodFouls' && (
        <>
          <LongDivider />
          <Item>
            <ChoosingOrientation onChange={changeOrientation} value={orientation} />
          </Item>
          <LongDivider />
          <Item>
            <UDText type={'caption-1'}>Максимальное кол-во индикаторов</UDText>
            <NumberInput
              value={maxCount}
              onChange={changeMaxCount}
              onFocus={onFocusField}
              min={1}
              max={20}
              step={1}
              precision={0}
              style={{ marginBottom: 10 }}
            />

            <UDText type={'caption-1'}>Расстояние между индикаторами (px)</UDText>
            <NumberInput
              value={spacing}
              onChange={changeSpacing}
              onFocus={onFocusField}
              min={0}
              step={5}
              precision={0}
              style={{ marginBottom: 10 }}
            />

            <ChoosingShape onChange={changeShape} value={shape} />
          </Item>
        </>
      )}
      {(name === 'teamComposition' || name === 'teamPeriodFouls') && (
        <>
          <LongDivider />
          <Item>
            <UDText type={'caption-1'}>Цвет элемента</UDText>
            <ColorPicker color={elementColor} onChange={changeElementColor} />
            {name === 'teamComposition' && (
              <>
                <UDText type={'caption-1'}>Цвет фона</UDText>
                <ColorPicker color={backgroundColor} onChange={changeBackgroundColor} />
                <UDText type={'caption-1'}>Цвет рамки</UDText>
                <ColorPicker color={borderColor} onChange={changeBorderColor} />
                <UDText type={'caption-1'}>Цвет текста</UDText>
                <ColorPicker color={textColor} onChange={changeTextColor} />
              </>
            )}
          </Item>
        </>
      )}
      <LongDivider />
      <LayerControls object={object} />
      <LockPosition objects={[object]} onChange={setIsPositionLock} value={isPositionLock} />
      {isDev && showDebugInfo && (
        <Item>
          hasTeam: {hasTeam ? 'true' : 'false'},
          <br />
          data: <span style={{ whiteSpace: 'pre' }}>{JSON.stringify(object.data, null, 2)}</span>
        </Item>
      )}
    </>
  );
}

export default ComplexPlaceholder;
